import React, { useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Check, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';

const LEVEL_KEYS = ['beginner', 'some_chords', 'intermediate', 'advanced'];
const GOAL_KEYS = ['play_songs', 'fingerstyle', 'improvise', 'theory', 'mix'];
const GENRE_KEYS = ['pop', 'folk', 'fingerstyle', 'blues', 'jazz', 'classical'];

const STEP_COUNT = 3;

const OnboardingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, loading, onboardingCompleted, user, submitOnboarding } = useAuth();

  const [step, setStep] = useState(0);
  const [level, setLevel] = useState(null);
  const [goal, setGoal] = useState(null);
  const [genres, setGenres] = useState([]);
  const [saving, setSaving] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/auth" replace />;
  if (onboardingCompleted) return <Navigate to="/dashboard" replace />;

  const toggleGenre = (g) => {
    setGenres(prev => prev.includes(g) ? prev.filter(x => x !== g) : [...prev, g]);
  };

  const canAdvance =
    (step === 0 && level !== null) ||
    (step === 1 && goal !== null) ||
    (step === 2 && genres.length > 0);

  const next = async () => {
    if (step < STEP_COUNT - 1) {
      setStep(step + 1);
      return;
    }
    if (genres.length === 0) {
      toast.error(t('onboarding.chooseAtLeastOne'));
      return;
    }
    setSaving(true);
    try {
      await submitOnboarding({ level, goal, genres });
      navigate('/dashboard');
    } catch (err) {
      toast.error(t('onboarding.savingError'));
      setSaving(false);
    }
  };

  const back = () => setStep(Math.max(0, step - 1));

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-16 pb-12 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-10 pt-8">
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-amber/10 border border-amber/30 text-amber text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" />
            {t('onboarding.title')}{user ? `, ${user.username}` : ''}
          </div>
          <p className="text-zinc-500 text-sm font-mono">
            {t('onboarding.step', { current: step + 1, total: STEP_COUNT })}
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            {Array.from({ length: STEP_COUNT }).map((_, i) => (
              <div
                key={i}
                className={`h-1 rounded-full transition-all ${
                  i === step ? 'w-12 bg-amber' : i < step ? 'w-8 bg-amber/40' : 'w-8 bg-white/10'
                }`}
              />
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step-level"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center mb-3">
                {t('onboarding.levelTitle')}
              </h1>
              <p className="text-zinc-400 text-center mb-8">{t('onboarding.levelSubtitle')}</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {LEVEL_KEYS.map(key => (
                  <button
                    key={key}
                    onClick={() => setLevel(key)}
                    className={`text-left p-5 rounded-2xl border transition-all ${
                      level === key
                        ? 'bg-amber/15 border-amber'
                        : 'bg-white/[0.02] border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`onboarding-level-${key}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-heading font-bold text-white">
                          {t(`onboarding.levels.${key}.label`)}
                        </div>
                        <div className="text-zinc-400 text-sm mt-1">
                          {t(`onboarding.levels.${key}.desc`)}
                        </div>
                      </div>
                      {level === key && <Check className="w-5 h-5 text-amber shrink-0" />}
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step-goal"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center mb-3">
                {t('onboarding.goalTitle')}
              </h1>
              <p className="text-zinc-400 text-center mb-8">{t('onboarding.goalSubtitle')}</p>
              <div className="space-y-3">
                {GOAL_KEYS.map(key => (
                  <button
                    key={key}
                    onClick={() => setGoal(key)}
                    className={`w-full text-left p-5 rounded-2xl border transition-all ${
                      goal === key
                        ? 'bg-amber/15 border-amber'
                        : 'bg-white/[0.02] border-white/10 hover:border-white/30'
                    }`}
                    data-testid={`onboarding-goal-${key}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-heading font-bold text-white">
                          {t(`onboarding.goals.${key}.label`)}
                        </div>
                        <div className="text-zinc-400 text-sm mt-1">
                          {t(`onboarding.goals.${key}.desc`)}
                        </div>
                      </div>
                      {goal === key && <Check className="w-5 h-5 text-amber shrink-0" />}
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step-genres"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white text-center mb-3">
                {t('onboarding.genresTitle')}
              </h1>
              <p className="text-zinc-400 text-center mb-8">{t('onboarding.genresSubtitle')}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {GENRE_KEYS.map(key => {
                  const active = genres.includes(key);
                  return (
                    <button
                      key={key}
                      onClick={() => toggleGenre(key)}
                      className={`p-4 rounded-2xl border font-heading font-bold transition-all ${
                        active
                          ? 'bg-amber/15 border-amber text-amber'
                          : 'bg-white/[0.02] border-white/10 text-zinc-300 hover:border-white/30'
                      }`}
                      data-testid={`onboarding-genre-${key}`}
                    >
                      {t(`onboarding.genres.${key}`)}
                      {active && <Check className="w-4 h-4 inline-block ml-2" />}
                    </button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex items-center justify-between gap-4 mt-12">
          <Button
            variant="ghost"
            onClick={back}
            disabled={step === 0}
            className="text-zinc-400 hover:text-white"
            data-testid="onboarding-back"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            {t('onboarding.back')}
          </Button>
          <Button
            onClick={next}
            disabled={!canAdvance || saving}
            className="bg-gradient-to-r from-amber to-orange-500 text-black font-bold disabled:opacity-40"
            data-testid="onboarding-next"
          >
            {saving ? (
              <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin mr-2" />
            ) : null}
            {step === STEP_COUNT - 1 ? t('onboarding.finish') : t('onboarding.next')}
            {step < STEP_COUNT - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
